"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useMemo } from "react";
import dynamic from "next/dynamic";
import { Loader2 } from "lucide-react";
import { useFansUnitedSDK } from "fansunited-data-layer/client";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { mapTenantToFUWidgetTheme } from "@/features/theme/fu-widget-theme";
import { useLocale } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useAuth } from "@/lib/auth/AuthContext";
import type { LocaleCode } from "@/lib/labels/types";
import { getColorModeFromHex } from "@/utilities/styles/colorMode";
const Discussion = dynamic(() => import("fansunited-frontend-components").then(m => ({
  default: m.Discussion
})), {
  ssr: false
});
type WidgetLanguage = "bg" | "en" | "ro" | "pt" | "sr" | "fr" | "de" | "it" | "fr-be" | "pl" | "pt-br" | "sk";
const localeToWidgetLanguage: Partial<Record<LocaleCode, WidgetLanguage>> = {
  "pt-BR": "pt-br"
};
function getWidgetLanguage(locale: LocaleCode): WidgetLanguage {
  return localeToWidgetLanguage[locale] ?? locale as WidgetLanguage;
}
interface DiscussionWidgetProps {
  entityId: string;
  discussionLabel: string;
  className?: string;
}
export const DiscussionWidget: React.FC<DiscussionWidgetProps> = t0 => {
  const $ = _c(25);
  const {
    entityId,
    discussionLabel,
    className
  } = t0;
  const [mounted, setMounted] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setMounted(true);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const sdk = useFansUnitedSDK();
  const locale = useLocale();
  let t3;
  if ($[2] !== locale) {
    t3 = getWidgetLanguage(locale);
    $[2] = locale;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const language = t3;
  const tenantSettings = useTenantSettings();
  const nav = useNavigation();
  const {
    user,
    isAuthEnabled
  } = useAuth();
  let t4;
  if ($[4] !== tenantSettings.namedColors) {
    const cardColor = tenantSettings.namedColors.find(_temp)?.value;
    t4 = getColorModeFromHex(cardColor);
    $[4] = tenantSettings.namedColors;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const mode = t4;
  let t5;
  if ($[6] !== tenantSettings) {
    t5 = mapTenantToFUWidgetTheme(tenantSettings);
    $[6] = tenantSettings;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const themeOptions = t5;
  let t6;
  if ($[8] !== nav) {
    t6 = () => {
      window.location.href = nav.signIn();
    };
    $[8] = nav;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleSignInClick = t6;
  if (!isAuthEnabled) {
    return null;
  }
  if (!mounted || !sdk) {
    let t7;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="flex items-center justify-center p-8"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    return t7;
  }
  const t7 = `${className} p-4 bg-card`;
  let t8;
  if ($[11] !== mode || $[12] !== themeOptions) {
    t8 = {
      ...themeOptions,
      mode
    };
    $[11] = mode;
    $[12] = themeOptions;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = !!user;
  let t10;
  if ($[14] !== discussionLabel || $[15] !== entityId || $[16] !== handleSignInClick || $[17] !== language || $[18] !== sdk || $[19] !== t8 || $[20] !== t9) {
    t10 = <Discussion entityId={entityId} sdk={sdk} language={language} discussionLabel={discussionLabel} themeOptions={t8} userIsLoggedIn={t9} showReactions={true} showReplies={true} showReportPost={true} defaultSort="LATEST" onSignInClick={handleSignInClick} />;
    $[14] = discussionLabel;
    $[15] = entityId;
    $[16] = handleSignInClick;
    $[17] = language;
    $[18] = sdk;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t7) {
    t11 = <div className={t7}>{t10}</div>;
    $[22] = t10;
    $[23] = t7;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
};
function _temp(c) {
  return c.name === "card";
}